import React, { useCallback, useState, useEffect, memo, useMemo } from "react";
import axios from "axios";
import localAppGalleryData from "../../AngelAppSection/AppGalleryDesktop/AppGallery.json";

/**
 * ComicBubble Component
 *
 * This component displays a speech bubble with various links to different workflows.
 * It fetches and manages its own data for the workflow links.
 *
 * @param {boolean} showComicBubble - Whether to display the comic bubble
 * @param {boolean} expanded - Whether the list of links is expanded
 * @param {Function} toggleExpand - Function to toggle the expanded state
 * @param {boolean} isMobile - Whether the current view is on a mobile device
 * @param {string} angelaiUrl - Base URL for AngelAI workflows
 * @param {string} typingText - Text displayed in the typing animation
 * @param {number} animationPhase - Current phase of the typing animation
 * @param {Object} [comicBubbleStyle] - Optional: Style object for the comic bubble
 */
const ComicBubble = memo(
  ({
    showComicBubble,
    expanded,
    toggleExpand,
    isMobile,
    angelaiUrl,
    typingText,
    animationPhase,
    comicBubbleStyle = null, // Accept style prop with default value
  }) => {
    // State for app gallery data
    const [appGalleryData, setAppGalleryData] = useState([]);

    // Fetch app gallery data only once
    useEffect(() => {
      const fetchAppGalleryData = async () => {
        try {
          const timestamp = Date.now();
          const url = `${process.env.REACT_APP_CDN_WEB}angelai-new-design/AppGallery/AppGallery.json?timestamp=${timestamp}`;
          const response = await axios.get(url);
          setAppGalleryData(response.data);
        } catch (cdnError) {
          console.warn("Failed to fetch AppGallery.json from CDN:", cdnError);
          try {
            const localApiResponse = await axios.get("/api/appgallery");
            setAppGalleryData(localApiResponse.data);
          } catch (localApiError) {
            console.warn(
              "Failed to fetch from local API endpoint:",
              localApiError
            );
            setAppGalleryData(localAppGalleryData);
          }
        }
      };
      fetchAppGalleryData();
    }, []);

    // Text to workflow name mapping
    const textToWorkflowNameMap = {
      "Buy a house": "Loan Application",
      "Lower your payments": "Refiance Calculator",
      "Take cash out": "Refiance Calculator",
      "Fix your credit": "Credit Boost",
      "Interest Rates": "Know Your Rate",
      "Rent or Buy Wizard": "Rent Vs Buy Calculator",
      "Home Value Finder": "Zestimate",
      "Payment Calculator": "Fixed Rate Calculator",
      "Training Zone": "Workshop Registration",
      "Lead Call Assist": "Call Lead Prospects",
      "Appraisal Pay Express": "Pay for Appraisal",
      "AngelAi Influencer Hub": "Influencer Workflow",
      "Angel Wallet": "Open User Wallet",
      "Custom Website for Loan Officer": "Broker Landing Page",
      "Affordability Advisor": "Affordibility Calculator",
      "Angel Twin": "Create My Twin",
      "Listing Site Builder": "Create Property Listing",
      "Daily Prize Spin": "Slot Machine",
      "Claim Your Relax Ai-Shirt": "Order T-Shirt",
    };

    // Filter available workflow items based on fetched data
    const availableWorkflowItems = useMemo(() => {
      const allWorkflowTexts = Object.keys(textToWorkflowNameMap);
      
      return allWorkflowTexts.filter((text) => {
        const workflowName = textToWorkflowNameMap[text];
        
        // If angelaiUrl is provided, assume all workflows are available
        if (angelaiUrl && workflowName) return true;
        
        // Otherwise, check if the workflow exists in the fetched data
        return appGalleryData.some(item => item.workflowName === workflowName);
      });
    }, [appGalleryData, angelaiUrl]);

    // Updated getWorkflowLink function with angelaiUrl logic
    const getWorkflowLink = useCallback(
      (text) => {
        const workflowNameToMatch = textToWorkflowNameMap[text];

        // If angelaiUrl is provided, use it with the workflow name pattern
        if (angelaiUrl && workflowNameToMatch) {
          // Replace spaces with %20 for URL encoding
          const encodedWorkflowName = workflowNameToMatch.replace(/ /g, "%20");
          return `${angelaiUrl}/${encodedWorkflowName}?opts=w`;
        }

        // Fallback to existing logic if angelaiUrl is not provided
        const matchingItem = appGalleryData.find(
          (item) => item.workflowName === workflowNameToMatch
        );

        return matchingItem ? matchingItem.workflowLink : "#";
      },
      [appGalleryData, angelaiUrl]
    );

    // Split available items into basic and expanded
    const basicItems = availableWorkflowItems.slice(0, 4);
    const expandedItems = availableWorkflowItems.slice(4);

    // Default comic bubble style if none is provided
    const defaultComicBubbleStyle = {
      position: "absolute",
      top: isMobile ? "-97px" : "-91px",
      left: isMobile ? "0" : "-3px",
      background: "#F9CCB3",
      color: "#000",
      padding: "10px 6px",
      borderRadius: "15px",
      width: isMobile ? "calc(100% - 0px)" : "495px",
      maxWidth: "460px",
      fontSize: isMobile ? "14px" : "16px",
      fontWeight: "500",
      border: "1px solid #FF541C",
      zIndex: 3,
      display: showComicBubble ? "block" : "none",
      transition: "all 0.3s ease",
    };

    // Use provided style or default style
    const bubbleStyle = comicBubbleStyle || defaultComicBubbleStyle;

    // Don't render anything if not showing the bubble (improves performance)
    if (!showComicBubble) {
      return null;
    }

    // Helper function to render a workflow item
    const renderWorkflowItem = (text, index, isInFirstRow = false) => (
      <div
        key={text}
        style={{
          display: "flex",
          alignItems: "flex-start",
          paddingBottom: "5px",
          marginTop: isInFirstRow ? "8px" : "0px",
        }}
      >
        <span
          style={{
            fontSize: "25px",
            marginRight: "5px",
            flex: "0 0 auto",
            lineHeight: "0.75",
            marginTop: "0",
          }}
        >
          •
        </span>
        <a
          href={getWorkflowLink(text)}
          target="_blank"
          rel="noreferrer"
          className="points-text-links"
          style={{
            display: "inline-block",
            lineHeight: "1.2",
          }}
        >
          <span className="points-text">{text}</span>
        </a>
      </div>
    );

    return (
      <div className="comic-bubble" style={bubbleStyle}>
        <div style={{ textAlign: "center" }}>
          <b>
            Ask me anything and hit send{" "}
            <svg
              width="12"
              height="12"
              viewBox="0 0 75 83"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="bubble-svg"
            >
              <path
                d="M27.1706 38.1746C22.5706 38.1746 17.9706 38.1346 13.3606 38.1846C9.46063 38.2246 6.89063 36.4446 6.03063 32.6646C4.40063 25.5446 2.80063 18.4046 1.45063 11.2146C0.760633 7.57462 1.86063 4.30462 5.09063 2.06462C8.45063 -0.265378 11.5606 0.734622 14.8106 2.58462C32.6306 12.7246 50.4806 22.8046 68.3906 32.7946C71.9606 34.7846 74.2406 37.4446 74.2706 41.5746C74.3006 45.7446 71.9406 48.3346 68.3706 50.3146C50.3306 60.3146 32.3406 70.3946 14.3706 80.5246C11.0306 82.4046 7.75063 83.7046 4.36063 80.9746C1.13063 78.3646 0.360633 74.9046 1.33063 70.9146C2.89063 64.5346 4.23063 58.0946 5.90063 51.7346C7.38063 46.1046 8.86063 45.1046 14.5306 45.0846C22.7406 45.0546 30.9506 45.0446 39.1606 45.0246C39.9106 45.0246 40.6706 45.1146 41.4006 44.9946C43.5106 44.6546 46.3406 45.0546 46.3306 41.7846C46.3206 38.6246 43.9606 38.1346 41.3606 38.1446C36.6306 38.1746 31.9106 38.1446 27.1806 38.1446V38.1646L27.1706 38.1746Z"
                fill="white"
              ></path>
            </svg>
          </b>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: "3px",
              rowGap: "0px",
              marginTop: "-6px",
              textAlign: "left",
              marginLeft: isMobile ? "5px" : "2.5rem",
            }}
          >
            {/* Render basic items (first 4 available) */}
            {basicItems.map((text, index) => 
              renderWorkflowItem(text, index, index < 2)
            )}

            {/* Render expanded items if expanded */}
            {expanded && expandedItems.map((text, index) => 
              renderWorkflowItem(text, index, false)
            )}
          </div>

          {/* Show More/Less button only if there are expandable items */}
          {expandedItems.length > 0 && (
            <div style={{ textAlign: "right", marginTop: "-5px" }}>
              <button
                onClick={toggleExpand}
                style={{
                  color: "inherit",
                  textDecoration: "underline",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "12.5px",
                  color: "#000",
                }}
              >
                {expanded ? "Read Less" : "Read More"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default ComicBubble;
